import axios from "axios";
import { getOrCreateSessionId } from "./deviceFingerprint";
import * as Sentry from "@sentry/react";
import getUserFriendlyError from "./errorSanitizer";

const { logger } = Sentry;

// Global redirect tracker to help debug - persists to localStorage
const REDIRECT_LOG_KEY = "leebai_redirect_log";
const MAX_LOG_ENTRIES = 50;

const logRedirect = (reason, url) => {
  try {
    // Get existing logs
    const existingLogs = JSON.parse(
      localStorage.getItem(REDIRECT_LOG_KEY) || "[]"
    );

    const logEntry = {
      reason,
      url,
      timestamp: new Date().toISOString(),
      pathname: window.location.pathname,
      stack: new Error().stack?.split("\n").slice(0, 10).join("\n"), // First 10 lines of stack
    };

    existingLogs.push(logEntry);

    // Keep only last MAX_LOG_ENTRIES
    if (existingLogs.length > MAX_LOG_ENTRIES) {
      existingLogs.shift();
    }

    localStorage.setItem(REDIRECT_LOG_KEY, JSON.stringify(existingLogs));
    
    // Also log to Sentry
    logger.error('[REDIRECT TRACKER] Redirect detected', logEntry);
    
    // Show alert if too many redirects
    if (existingLogs.length > 3) {
      logger.error('[REDIRECT TRACKER] Multiple redirects detected! Check localStorage for full log.');
    }
  } catch (e) {
    Sentry.captureException(e);
    logger.error('[REDIRECT TRACKER] Error saving log', { error: e.message });
  }
};

// Enhanced logging that persists to localStorage
const persistentLog = (level, category, message, data = {}) => {
  try {
    const LOG_KEY = "leebai_debug_log";
    const existingLogs = JSON.parse(localStorage.getItem(LOG_KEY) || "[]");

    const logEntry = {
      level,
      category,
      message,
      data,
      timestamp: new Date().toISOString(),
      pathname: window.location.pathname,
    };

    existingLogs.push(logEntry);

    // Keep only last 100 entries
    if (existingLogs.length > 100) {
      existingLogs.shift();
    }

    localStorage.setItem(LOG_KEY, JSON.stringify(existingLogs));
    
    // Also log to Sentry
    const logMethod = level === 'error' ? logger.error : level === 'warn' ? logger.warn : logger.info;
    logMethod(`[${category}] ${message}`, data);
  } catch (e) {
    // Fallback to Sentry logger only
    logger.info(`[${category}] ${message}`, data);
  }
};

// Helper function to get request headers (supports both authenticated and anonymous users)
const getRequestHeaders = (additionalHeaders = {}) => {
  const authToken = JSON.parse(localStorage.getItem("user"));
  const headers = { ...additionalHeaders };

  if (authToken?.token) {
    // Authenticated user
    headers.Authorization = `Bearer ${authToken.token}`;
  } else {
    // Anonymous user - include session headers
    const { sessionId, deviceFingerprint } = getOrCreateSessionId();
    headers["x-session-id"] = sessionId;
    headers["x-device-fingerprint"] = deviceFingerprint;
  }

  return headers;
};

// Helper to get auth token (called at function execution time, not module load)
const getAuthToken = () => {
  try {
    const user = localStorage.getItem("user");
    return user ? JSON.parse(user) : null;
  } catch {
    return null;
  }
};

export const authHttpPost = async ({ url, payload = null }) => {
  try {
    const response = await axios.post(url, payload);
    return response;
  } catch (e) {
    Sentry.captureException(e);
    
    // Also store locally for admin dashboard
    if (window.addSentryEvent) {
      window.addSentryEvent({
        id: `http-error-${Date.now()}`,
        title: `HTTP Error: ${e.message}`,
        message: e.message,
        category: 'api_error',
        level: 'error',
        timestamp: new Date().toISOString(),
        user: {
          id: 'current-user',
          email: JSON.parse(localStorage.getItem('user') || '{}')?.email || 'unknown',
          username: JSON.parse(localStorage.getItem('user') || '{}')?.name || 'unknown'
        },
        context: {
          api_endpoint: url,
          method: 'POST',
          status_code: e.response?.status,
          error_message: e.message
        },
        count: 1,
        firstSeen: new Date().toISOString(),
        lastSeen: new Date().toISOString()
      });
    }
    
    logger.error("authHttpPost error", { url, error: e.message });
    
    // Sanitize error message for user
    if (e.response?.data) {
      e.response.data.message = getUserFriendlyError(e);
      e.response.data.error = getUserFriendlyError(e);
    }
    
    return e.response;
  }
};

export const httpGet = async ({ url, params = null }) => {
  persistentLog("log", "httpGet", "📡 Request initiated", { url, params });

  // Allow anonymous users for certain endpoints (chat-related, models, and personas)
  // These endpoints are needed for anonymous users to use the chat interface
  const isChatEndpoint = url.includes("/chat/") || url.includes("/api/chat");
  const isModelEndpoint =
    url.includes("/models") || url.includes("/api/models");
  const isPersonaEndpoint =
    url.includes("/personas") || url.includes("/api/personas");
  const isAnonymousAllowedEndpoint =
    isChatEndpoint || isModelEndpoint || isPersonaEndpoint;

  let authToken = null;
  try {
    const userData = localStorage.getItem("user");
    authToken = userData ? JSON.parse(userData) : null;
  } catch (e) {
    Sentry.captureException(e);
    logger.error('[httpGet] Error parsing user data', { error: e.message });
  }
  
  persistentLog('log', 'httpGet', '🔍 Auth check', {
    url,
    isChatEndpoint,
    isModelEndpoint,
    isAnonymousAllowedEndpoint,
    hasAuthToken: !!authToken,
    hasToken: !!authToken?.token,
  });

  if (!authToken && !isAnonymousAllowedEndpoint) {
    persistentLog(
      "error",
      "httpGet",
      "❌ BLOCKING: No auth token and not an allowed endpoint - redirecting to login",
      {
        url,
        isChatEndpoint,
        isModelEndpoint,
        isAnonymousAllowedEndpoint,
      }
    );
    logRedirect("httpGet: No auth token and not allowed endpoint", url);

    // Add delay to allow logs to be saved
    setTimeout(() => {
      window.location.href = window.location.origin + "/login";
    }, 100);
    return; // Ensure function stops here
  }

  if (!authToken && isAnonymousAllowedEndpoint) {
    persistentLog("log", "httpGet", "✅ Allowing anonymous access", {
      url,
      endpointType: isChatEndpoint ? "chat" : "model",
    });
  }

  try {
    const headers = getRequestHeaders();
    persistentLog("log", "httpGet", "✅ Proceeding with request", {
      url,
      hasAuthHeader: !!headers.Authorization,
      hasSessionHeaders: !!(
        headers["x-session-id"] || headers["x-device-fingerprint"]
      ),
    });

    const response = await axios.get(url, {
      headers,
      params: params,
    });

    persistentLog("log", "httpGet", "✅ Request successful", {
      url,
      status: response.status,
    });

    return response;
  } catch (e) {
    persistentLog("error", "httpGet", "❌ Request failed", {
      url,
      status: e.response?.status,
      statusText: e.response?.statusText,
      error: e.response?.data?.error || e.message,
      hasAuthToken: !!authToken,
      isAnonymousAllowedEndpoint,
      willRedirect: !!(e.response && e.response.status === 401 && authToken),
    });

    // For anonymous users on allowed endpoints, don't redirect on 401
    // Just throw the error so the calling code can handle it
    if (e.response && e.response.status === 401) {
      if (authToken) {
        // Authenticated user got 401 - redirect to login
        persistentLog(
          "error",
          "httpGet",
          "❌ 401 Unauthorized for authenticated user - redirecting to login",
          { url }
        );
        logRedirect("httpGet: 401 for authenticated user", url);

        // Add delay to allow logs to be saved
        setTimeout(() => {
          window.location.href = window.location.origin + "/login";
          localStorage.clear();
        }, 100);
        return; // Don't throw, just return
      } else if (isAnonymousAllowedEndpoint) {
        // Anonymous user on allowed endpoint got 401 - backend might not support anonymous
        // Log but don't redirect - let the calling code handle it
        persistentLog(
          "warn",
          "httpGet",
          "⚠️ 401 for anonymous user on allowed endpoint - backend may not support anonymous access",
          { url }
        );
        throw e; // Throw so calling code can handle gracefully
      } else {
        // Anonymous user on non-allowed endpoint - should have been blocked earlier
        Sentry.captureException(e);
        logger.error('[httpGet] Unexpected 401 for anonymous user on non-allowed endpoint', { url });
        throw e;
      }
    } else {
      // Sanitize error message before throwing
      const friendlyError = new Error(getUserFriendlyError(e));
      friendlyError.response = e.response;
      if (friendlyError.response?.data) {
        friendlyError.response.data.message = getUserFriendlyError(e);
        friendlyError.response.data.error = getUserFriendlyError(e);
      }
      throw friendlyError;
    }
  }
};

export const httpPostBlob = async ({ url, payload }) => {
  // Allow anonymous users for chat endpoints
  const isChatEndpoint = url.includes("/chat/");
  const authToken = JSON.parse(localStorage.getItem("user"));

  try {
    const headers = getRequestHeaders({
      "Content-Type": "multipart/form-data",
    });
    const response = await axios.post(url, payload, { headers });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpPostBlob error", { url, error: e.message });
    throw e;
  }
};

export const httpPostBlobDoc = async ({ url, payload = null }) => {
  const authToken = getAuthToken();
  if (!authToken) {
    window.location.href = window.location.origin + "/login";
    return;
  }
  try {
    const response = await axios.post(url, payload, {
      headers: {
        Authorization: `Bearer ${authToken.token}`,
      },
      responseType: "blob",
    });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpPostBlobDoc error", { url, error: e.message });
    throw e;
  }
};

export const httpPost = async ({ url, payload = null }) => {
  logger.info('[httpPost] Request initiated', { url, timestamp: new Date().toISOString() });
  
  // Allow anonymous users for chat endpoints
  const isChatEndpoint = url.includes("/chat/") || url.includes("/api/chat");
  let authToken = null;
  try {
    const userData = localStorage.getItem("user");
    authToken = userData ? JSON.parse(userData) : null;
  } catch (e) {
    Sentry.captureException(e);
    logger.error('[httpPost] Error parsing user data', { error: e.message });
  }
  
  logger.debug('[httpPost] Auth check', {
    url,
    isChatEndpoint,
    hasAuthToken: !!authToken,
    hasToken: !!authToken?.token
  });
  
  if (!authToken && !isChatEndpoint) {
    logger.error('[httpPost] BLOCKING: No auth token and not a chat endpoint - redirecting to login', {
      url,
      isChatEndpoint
    });
    logRedirect('httpPost: No auth token and not chat endpoint', url);
    window.location.href = window.location.origin + "/login";
    return;
  }

  try {
    const headers = getRequestHeaders();
    const response = await axios.post(url, payload, { headers });
    return response;
  } catch (e) {
    if (e.response && e.response.status === 401 && authToken) {
      // Only redirect to login if user was authenticated
      logger.error('[httpPost] 401 Unauthorized for authenticated user - redirecting to login');
      logRedirect('httpPost: 401 for authenticated user', url);
      window.location.href = window.location.origin + "/login";
      localStorage.clear();
      return; // Don't throw, just return
    } else {
      Sentry.captureException(e);
      logger.warn('[httpPost] Error (not redirecting)', { url, error: e.message });
      
      // Sanitize error message before throwing
      const friendlyError = new Error(getUserFriendlyError(e));
      friendlyError.response = e.response;
      if (friendlyError.response?.data) {
        friendlyError.response.data.message = getUserFriendlyError(e);
        friendlyError.response.data.error = getUserFriendlyError(e);
      }
      throw friendlyError;
    }
  }
};

export const httpPut = async ({ url, payload }) => {
  const authToken = getAuthToken();
  if (!authToken) {
    window.location.href = window.location.origin + "/login";
    return;
  }
  try {
    const response = await axios.put(url, payload, {
      headers: { Authorization: "Bearer " + authToken.token },
    });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpPut error", { url, error: e.message });
    
    // Sanitize error message before throwing
    const friendlyError = new Error(getUserFriendlyError(e));
    friendlyError.response = e.response;
    if (friendlyError.response?.data) {
      friendlyError.response.data.message = getUserFriendlyError(e);
      friendlyError.response.data.error = getUserFriendlyError(e);
    }
    throw friendlyError;
  }
};

export const httpDelete = async ({ url, payload = null }) => {
  const authToken = getAuthToken();
  if (!authToken) {
    window.location.href = window.location.origin + "/login";
    return;
  }
  try {
    const config = {
      headers: {
        Authorization: "Bearer " + authToken.token,
        "Content-Type": "application/json",
      },
    };

    // Only add data if payload is not null
    if (payload !== null && payload !== undefined) {
      config.data = payload;
    }

    const response = await axios.delete(url, config);
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpDelete error", { url, error: e.message });
    
    // Sanitize error message before throwing
    const friendlyError = new Error(getUserFriendlyError(e));
    friendlyError.response = e.response;
    if (friendlyError.response?.data) {
      friendlyError.response.data.message = getUserFriendlyError(e);
      friendlyError.response.data.error = getUserFriendlyError(e);
    }
    throw friendlyError;
  }
};

export const httpPatch = async ({ url, payload = null }) => {
  const authToken = getAuthToken();
  if (!authToken) {
    window.location.href = window.location.origin + "/login";
    return;
  }
  try {
    const response = await axios.patch(url, payload, {
      headers: { Authorization: "Bearer " + authToken.token },
    });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpPatch error", { url, error: e.message });
    
    // Sanitize error message
    if (e.response?.data) {
      e.response.data.message = getUserFriendlyError(e);
      e.response.data.error = getUserFriendlyError(e);
    }
    return e;
  }
};

export const httpGetBlob = async ({ url, params = null }) => {
  const authToken = getAuthToken();
  if (!authToken) {
    window.location.href = window.location.origin + "/login";
    return;
  }

  try {
    const response = await axios.get(url, {
      headers: { Authorization: "Bearer " + authToken.token },
      params: params,
      responseType: "blob",
    });
    return response;
  } catch (e) {
    if (e.response && e.response.status === 401) {
      window.location.href = window.location.origin + "/login";
      localStorage.clear();
    } else {
      Sentry.captureException(e);
      logger.error("Error fetching blob", { url, error: e.message });
    }
    return e;
  }
};

export const httpGetWithOutToken = async ({ url, params = null }) => {
  // if (!authToken) {
  //   window.location.href = window.location.origin + '/login';
  // }
  try {
    const response = await axios.get(url, {
      // headers: { Authorization: 'Bearer ' + authToken.access_token },
      params: params,
    });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpGetWithOutToken error", { url, error: e.message });
    throw e; // Throw the error instead of returning undefined
  }
};

export const httpPostWithOutToken = async ({ url, payload = null }) => {
  // if (!authToken) {
  //   window.location.href = window.location.origin + '/login';
  // }
  try {
    const response = await axios.post(url, payload, {
      // headers: { Authorization: 'Bearer ' + authToken.access_token }
    });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpPostWithOutToken error", { url, error: e.message });
    throw e; // Throw the error instead of returning it
  }
};
export const httpPutWithOutToken = async ({ url, payload = null }) => {
  // if (!authToken) {
  //   window.location.href = window.location.origin + '/login';
  // }
  try {
    const response = await axios.put(url, payload, {
      // headers: { Authorization: 'Bearer ' + authToken.access_token }
    });
    return response;
  } catch (e) {
    Sentry.captureException(e);
    logger.error("httpPutWithOutToken error", { url, error: e.message });
    return e;
  }
};

export const httpPostStream = async ({
  url,
  payload,
  onMessage,
  headers = {},
  abortController,
}) => {
  let fetchHeaders = getRequestHeaders(headers);
  let body = payload;
  // If not FormData, assume JSON
  if (!(payload instanceof FormData)) {
    fetchHeaders["Content-Type"] = "application/json";
    body = JSON.stringify(payload);
  }
  const response = await fetch(url, {
    method: "POST",
    headers: fetchHeaders,
    body,
    signal: abortController?.signal,
  });
  // If backend responds with an error status (e.g., 4xx/5xx), surface it upstream
  if (!response.ok) {
    let errorPayload = null;
    try {
      const text = await response.text();
      try {
        errorPayload = text ? JSON.parse(text) : null;
      } catch {
        errorPayload = { error: text };
      }
    } catch {
      errorPayload = { error: response.statusText };
    }
    // Sanitize error message
    const originalMessage = (errorPayload && (errorPayload.error || errorPayload.message)) ||
      response.statusText ||
      "Request failed";
    const friendlyMessage = getUserFriendlyError({ 
      message: originalMessage, 
      response: { status: response.status, data: errorPayload } 
    });
    
    const err = new Error(friendlyMessage);
    err.response = { 
      status: response.status, 
      data: { ...errorPayload, message: friendlyMessage, error: friendlyMessage }
    };
    throw err;
  }
  if (!response.body) throw new Error("No response body for streaming");
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let isDone = false;
  let currentEvent = null;
  while (!isDone) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // Process SSE lines (split on single newline)
    let boundary;
    while ((boundary = buffer.indexOf("\n")) !== -1) {
      const message = buffer.slice(0, boundary).trim();
      buffer = buffer.slice(boundary + 1);
      if (message.startsWith("event: reasoning")) {
        currentEvent = "reasoning";
        continue;
      }
      if (message.startsWith("event: model_switch")) {
        currentEvent = "model_switch";
        continue;
      }
      if (message.startsWith("data: ")) {
        const data = message.slice(6); // Use slice(6) to be more precise than replace
        if (data === "[DONE]") {
          isDone = true;
          onMessage && onMessage(null, true);
        } else {
          if (currentEvent === "reasoning") {
            // Reasoning tokens come as plain text chunks
            const unescapedData = data
              .replace(/\\n/g, "\n")
              .replace(/^data:\s*/g, "")
              .replace(/=data:/g, " = "); // Handle any remaining =data: patterns
            onMessage &&
              onMessage(null, false, {
                type: "reasoning",
                content: unescapedData,
              });
            currentEvent = null;
          } else if (currentEvent === "model_switch") {
            // Model switch event - parse JSON and pass to handler
            try {
              const modelSwitchData = JSON.parse(data);
              logger.info("[httpPostStream] Parsed model_switch event", modelSwitchData);
              onMessage && onMessage(null, false, modelSwitchData);
            } catch (e) {
              Sentry.captureException(e);
              logger.error("[httpPostStream] Failed to parse model_switch event", { error: e.message, rawData: data });
            }
            currentEvent = null;
          } else {
            // Try to parse as JSON first (for special data like citations)
            try {
              const jsonData = JSON.parse(data);
              if (jsonData.type) {
                // This is structured data (like citations)
                onMessage && onMessage(null, false, jsonData);
              } else {
                // Regular JSON data, treat as text
                onMessage && onMessage(data, false);
              }
            } catch (e) {
              // Not JSON, treat as regular text
              // Unescape newlines that were escaped in the backend
              // Also filter out any remaining "data:" prefixes that might have leaked through
              const unescapedData = data
                .replace(/\\n/g, "\n")
                .replace(/^data:\s*/g, "")
                .replace(/=data:/g, " = "); // Handle any remaining =data: patterns
              onMessage && onMessage(unescapedData, false);
            }
          }
        }
      } else if (message.startsWith("event: truncated")) {
        // Custom event for truncated response
        // No data payload expected, just the event
        onMessage && onMessage(null, { truncated: true });
      } else if (message.startsWith("event: pruned")) {
        // Custom event for pruned response
        // No data payload expected, just the event
        onMessage && onMessage(null, { pruned: true });
      }
    }
  }
};
